import React, { useEffect, useRef, useState } from 'react';
import { Building2, GraduationCap, Megaphone, Users } from 'lucide-react';
import Reveal from './Reveal';

const CountUp = ({ end = 0, duration = 1400 }) => {
  const [value, setValue] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !end) return;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setValue(Math.floor(progress * end));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [visible, end, duration]);

  return <span ref={ref}>{value.toLocaleString('en-US')}</span>;
};

const ICONS_BY_KEY = { active_clubs: Building2, academies_count: GraduationCap, active_coaches: Megaphone, total_players: Users };
const DEFAULT_STATS_ITEMS = [
  { key: 'active_clubs', label: 'منشأة رياضية نشطة', visible: true, order: 1 },
  { key: 'academies_count', label: 'أكاديمية مسجلة', visible: true, order: 2 },
  { key: 'active_coaches', label: 'مدرب نشط بالمنصة', visible: true, order: 3 },
  { key: 'total_players', label: 'لاعب يُدار عبر المنصة', visible: true, order: 4 },
];

export const LandingStats = ({ landing, stats }) => {
  const configured = Array.isArray(landing?.stats_items) && landing.stats_items.length ? landing.stats_items : DEFAULT_STATS_ITEMS;
  const items = [...configured]
    .filter((it) => it.visible !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((it) => ({ icon: ICONS_BY_KEY[it.key] || Building2, value: stats?.[it.key] ?? 0, label: it.label }));

  if (items.length === 0) return null;

  return (
    <div className="relative z-20 mx-auto -mt-8 max-w-6xl px-5 sm:-mt-16" data-testid="landing-stats">
      <Reveal>
        <div className="grid grid-cols-2 gap-8 rounded-2xl border border-teal-900/30 bg-[#0A192F]/90 p-8 shadow-2xl shadow-black/40 backdrop-blur-xl sm:grid-cols-4 sm:gap-4 sm:divide-x sm:divide-x-reverse sm:divide-white/10">
          {items.map((it, i) => (
            <div key={i} className="px-2 text-center sm:px-4" data-testid={`landing-stat-${i}`}>
              <it.icon className="mx-auto mb-2 h-6 w-6 text-teal-400" />
              <div className="font-display text-3xl font-black text-white sm:text-4xl">
                <CountUp end={it.value} />+
              </div>
              <div className="mt-1 text-xs text-slate-400 sm:text-sm">{it.label}</div>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  );
};

export default LandingStats;
